import { useSyncExternalStore } from 'react';

/**
 * Below this, a shrinking visual viewport is browser chrome settling — the URL bar collapsing,
 * a toolbar sliding away — rather than a keyboard. The smallest on-screen keyboard is well over it.
 */
const KEYBOARD_THRESHOLD = 120;

function subscribe(onChange: () => void) {
  const viewport = window.visualViewport;
  if (!viewport) return () => {};
  viewport.addEventListener('resize', onChange);
  viewport.addEventListener('scroll', onChange);
  return () => {
    viewport.removeEventListener('resize', onChange);
    viewport.removeEventListener('scroll', onChange);
  };
}

/** The same measurement, for a caller that asks once rather than subscribing. */
export function keyboardInset(): number {
  return measure();
}

function measure(): number {
  const viewport = window.visualViewport;
  // Pinch-zoom shrinks the visual viewport too, and that is not a keyboard. A keyboard opened
  // while zoomed in goes unmeasured, and the browser's own scroll-to-focus is what is left.
  if (!viewport || Math.abs(viewport.scale - 1) > 0.01) return 0;
  const covered = window.innerHeight - viewport.height - viewport.offsetTop;
  return covered >= KEYBOARD_THRESHOLD ? Math.round(covered) : 0;
}

/**
 * How many CSS pixels of the layout viewport the on-screen keyboard is covering, or 0.
 *
 * iOS Safari OVERLAYS the keyboard rather than resizing the page, and the document here is locked
 * to 100% with `overflow: hidden` (see styles.css), so a vertically centred form stays centred on a
 * screen that is half keyboard — the password field and the button end up underneath it.
 * `interactive-widget=resizes-content` would fix it on Chromium but Safari ignores it, so the
 * visual viewport is the one signal that works on both.
 */
export function useKeyboardInset(): number {
  return useSyncExternalStore(subscribe, measure, () => 0);
}
